const getBaseURL = (): string => {
  const envURL = import.meta.env.VITE_API_BASE_URL || '';
  const isProductionBuild = import.meta.env.MODE === 'production';
  
  // Always reject localhost URLs in production builds
  // This prevents localhost from being baked into the build
  if (isProductionBuild && (envURL.includes('localhost') || envURL.includes('127.0.0.1'))) {
    return '';
  }
  
  // Additional runtime check: if running in production environment (not on localhost)
  // and somehow localhost URL is present, reject it
  if (typeof window !== 'undefined') {
    const isProductionRuntime = !window.location.hostname.includes('localhost') && 
                                !window.location.hostname.includes('127.0.0.1');
    
    if (isProductionRuntime && (envURL.includes('localhost') || envURL.includes('127.0.0.1'))) {
      return '';
    }
  }
  
  return envURL;
};

export const API_CONFIG = {
  // Use relative URLs in production (empty string) or absolute URL in development
  // In production, nginx proxies /api/ to backend, so relative URLs work
  baseURL: getBaseURL(),
  apiVersion: '/api/v1',
} as const;

export const API_ENDPOINTS = {
  projects: '/projects',
  wbs: '/wbs',
  orders: '/orders',
  projectLoggedHours: '/hours-entry',
  allTimeEntries: '/hours-entry/all',
  hoursEntry: '/employee/emp_id/:emp_id/time-entries',
  employeeStatusTimeEntries: '/employee/emp_id/:emp_id/time-entries/status',
  deleteTimeEntry: '/hours-entry/:id',
  loggedHours: '/logged-hours',
  employeeProjectModels: '/employee-project-models',
} as const;

export const getApiUrl = (endpoint: string, subPath?: string): string => {
  const url = `${API_CONFIG.baseURL}${API_CONFIG.apiVersion}${endpoint}`;
  return subPath ? `${url}${subPath}` : url;
};

export const apiUrl = {
  projects: (id?: string) => {
    const baseUrl = getApiUrl(API_ENDPOINTS.projects);
    if (id) {
      return `${baseUrl}/${id}`;
    }
    return baseUrl;
  },
  projectsByRole: () => {
    const baseUrl = getApiUrl(API_ENDPOINTS.projects);
    return `${baseUrl}/by-role`;
  },
  projectOrders: (projectId: string) => {
    const baseUrl = getApiUrl('/project');
    return `${baseUrl}/${projectId}/orders`;
  },
  wbs: (id?: string) => {
    const baseUrl = getApiUrl(API_ENDPOINTS.wbs);
    if (id) {
      return `${baseUrl}/${id}`;
    }
    return baseUrl;
  },
  orders: (id?: string) => {
    const baseUrl = getApiUrl(API_ENDPOINTS.orders);
    if (id) {
      return `${baseUrl}/${id}`;
    }
    return baseUrl;
  },
  projectLoggedHours: () => getApiUrl(API_ENDPOINTS.projectLoggedHours),

  allTimeEntries: () => getApiUrl(API_ENDPOINTS.allTimeEntries),
  hoursEntry: (empId: string, params?: Record<string, string> | string) => {
    const baseUrl = getApiUrl(API_ENDPOINTS.hoursEntry);
    const url = baseUrl.replace(':emp_id', empId);
    if (!params) return url;

    if (typeof params === 'string') {
      return `${url}${params}`;
    }

    const queryString = new URLSearchParams(
      Object.entries(params).map(([k, v]) => [k, String(v)])
    ).toString();
    return `${url}?${queryString}`;
  },

  employeeStatusTimeEntries: (empId: string, subpath?: string) => {
    const baseUrl = getApiUrl(API_ENDPOINTS.employeeStatusTimeEntries);
    const url = baseUrl.replace(':emp_id', empId);
    if (subpath) {
      return `${url}${subpath}`;
    }
    return url;
  },

  deleteTimeEntry: (id: string) => {
    const baseUrl = getApiUrl(API_ENDPOINTS.deleteTimeEntry);
    return baseUrl.replace(':id', id);
  },

  loggedHours: (id?: string) => {
    const baseUrl = getApiUrl(API_ENDPOINTS.loggedHours);
    if (id) {
      return `${baseUrl}/${id}`;
    }
    return baseUrl;
  },

  updateProjectLoggedHours: (id: string) => {
    const baseUrl = getApiUrl(API_ENDPOINTS.projectLoggedHours);
    return `${baseUrl}/${id}`;
  },
  clearAllProjectLoggedHours: (empId: string, date: string) => {
    const baseUrl = `${API_CONFIG.baseURL}${API_CONFIG.apiVersion}/employee/emp_id/${empId}/time-entries-all`;
    return `${baseUrl}?date=${encodeURIComponent(date)}`;
  },

  updateLoggedHours: (id: string) => {
    const baseUrl = getApiUrl(API_ENDPOINTS.loggedHours);
    return `${baseUrl}/${id}`;
  },

  employeeSchedule: (empId: string, year?: number) => {
    const baseUrl = `${API_CONFIG.baseURL}${API_CONFIG.apiVersion}/employee/emp_id/${empId}/schedule`;
    if (year) {
      return `${baseUrl}?year=${year}`;
    }
    return baseUrl;
  },
  closeWeekEntries: (empId: string, calendarWeek: string) => {
    const baseUrl = `${API_CONFIG.baseURL}${API_CONFIG.apiVersion}/employee/emp_id/${empId}/entries/close`;
    return `${baseUrl}?calendar_week=${encodeURIComponent(calendarWeek)}`;
  },
  closeWeekPreview: (empId: string, calendarWeek: string) =>
    `${API_CONFIG.baseURL}${API_CONFIG.apiVersion}/employee/emp_id/${empId}/entries/close-preview?calendar_week=${encodeURIComponent(calendarWeek)}`,
  adminLogStatus: (calendarWeek?: string) => {
    const baseUrl = `${API_CONFIG.baseURL}${API_CONFIG.apiVersion}/admin/log-status`;
    if (calendarWeek) {
      return `${baseUrl}?calendar_week=${encodeURIComponent(calendarWeek)}`;
    }
    return baseUrl;
  },
  adminLogProjects: () => {
    return `${API_CONFIG.baseURL}${API_CONFIG.apiVersion}/admin/log-projects`;
  },
  zmSyncStatus: () => `${API_CONFIG.baseURL}${API_CONFIG.apiVersion}/zm-sync/status`,
  adminZmSync: () => `${API_CONFIG.baseURL}${API_CONFIG.apiVersion}/admin/zm-sync`,
  adminZmSyncLog: (limit: number) => `${API_CONFIG.baseURL}${API_CONFIG.apiVersion}/admin/zm-sync/log?limit=${limit}`,
  zmChanges: (empId: string) => `${API_CONFIG.baseURL}${API_CONFIG.apiVersion}/employee/emp_id/${empId}/zm-changes`,
  zmChangesAck: (empId: string) => `${API_CONFIG.baseURL}${API_CONFIG.apiVersion}/employee/emp_id/${empId}/zm-changes/ack`,
  employeeYearOverview: (empId: string, year: number) => {
    return `${API_CONFIG.baseURL}${API_CONFIG.apiVersion}/employee/emp_id/${empId}/overview?year=${year}`;
  },
  employeeMonthOverview: (empId: string, year: number) => {
    return `${API_CONFIG.baseURL}${API_CONFIG.apiVersion}/employee/emp_id/${empId}/month-overview?year=${year}`;
  },
  employeeProjectOverview: (empId: string, year: number, month?: number | null) => {
    const baseUrl = `${API_CONFIG.baseURL}${API_CONFIG.apiVersion}/employee/emp_id/${empId}/project-overview?year=${year}`;
    if (month !== undefined && month !== null) {
      // Convert from 0-11 (JS month index) to 1-12 (calendar month)
      return `${baseUrl}&month=${month + 1}`;
    }
    return baseUrl;
  },
  employeeProjectLogHours: (empId: string, year: number, projectNumber?: string | null) => {
    const baseUrl = `${API_CONFIG.baseURL}${API_CONFIG.apiVersion}/employee/emp_id/${empId}/projectloghours?year=${year}`;
    if (projectNumber !== undefined && projectNumber !== null && projectNumber !== '') {
      return `${baseUrl}&projectnumber=${encodeURIComponent(projectNumber)}`;
    }
    return baseUrl;
  },
  employeeLogProjects: (empId: string, month?: number, year?: number, projectNumber?: string) => {
    const baseUrl = `${API_CONFIG.baseURL}${API_CONFIG.apiVersion}/employee/emp_id/${empId}/log-projects`;
    const params = new URLSearchParams();
    if (month !== undefined) {
      params.append('month', String(month));
    }
    if (year !== undefined) {
      params.append('year', String(year));
    }
    if (projectNumber !== undefined && projectNumber !== '') {
      params.append('project', projectNumber);
    }
    const queryString = params.toString();
    return queryString ? `${baseUrl}?${queryString}` : baseUrl;
  },
  favourites: () => {
    return `${API_CONFIG.baseURL}${API_CONFIG.apiVersion}/favourites`;
  },
  favourite: (id: string) => {
    return `${API_CONFIG.baseURL}${API_CONFIG.apiVersion}/favourites/${id}`;
  },
  employeeProjectModels: (id?: string) => {
    const baseUrl = getApiUrl(API_ENDPOINTS.employeeProjectModels);
    if (id) {
      return `${baseUrl}/${id}`;
    }
    return baseUrl;
  },
  projectReportSettings: (projectNumber?: string) => {
    const baseUrl = `${API_CONFIG.baseURL}${API_CONFIG.apiVersion}/project-report-settings`;
    if (projectNumber) {
      return `${baseUrl}/${projectNumber}`;
    }
    return baseUrl;
  },
  customerReports: {
    group: () => `${API_CONFIG.baseURL}${API_CONFIG.apiVersion}/customer-reports/group`,
    groupExportExcel: () => `${API_CONFIG.baseURL}${API_CONFIG.apiVersion}/customer-reports/group/export/excel`,
    isolated: (employeeId?: string) => {
      const baseUrl = `${API_CONFIG.baseURL}${API_CONFIG.apiVersion}/customer-reports/isolated`;
      if (employeeId) {
        return `${baseUrl}/${employeeId}`;
      }
      return baseUrl;
    },
    isolatedExportExcel: (employeeId: string) =>
      `${API_CONFIG.baseURL}${API_CONFIG.apiVersion}/customer-reports/isolated/${employeeId}/export/excel`,
  },
  employeeLogProjectsExportExcel: (empId: string) =>
    `${API_CONFIG.baseURL}${API_CONFIG.apiVersion}/employee/emp_id/${empId}/log-projects/export/excel`,
};
